import { useState, useMemo } from "react";
import styles from "./fba-assign-modal.module.css";
import { updateBoxV2 } from "@/api/fulfillment";
import IconBox from "@/components/icons/IconBox";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { usePrinterContext } from "@/contexts/PrinterContext";
import { FbaBox, getItemAssignedQuantity, useShipmentContext } from "@/contexts/ShipmentContext";
import Button from "@/components/forms/Button/Button";
import ProductImage from "@/components/ProductImage/ProductImage";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconCaution from "@/components/icons/IconCaution";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import { ShipmentUpdateBoxBody } from "@boxem/core-api-schema";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { clamp } from "@/utils/dateUtils";
import Flex from "@/components/Flex/Flex";
import { CreateNewBoxButton } from "../../CreateNewBoxButton/CreateNewBoxButton";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Alert from "@/components/Alert/Alert";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import { Item } from "../PrepAndBoxemPage/PackingGroup";

export type FbaAssignModalProps = {
  item?: Item;
  boxes: FbaBox[];
  packingGroupId?: string;
  shipmentId?: number;
  onClose?: () => void;
  createBoxes: (n: number) => Promise<void>;
}

enum BulkAssignMode { splitEvenly, assignManually };

export default function FbaAssignModal(props: FbaAssignModalProps) {
  const { item, boxes, packingGroupId, shipmentId, onClose, createBoxes } = props;

  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { printFNSKULabel } = usePrinterContext();
  const { currentBatch, setCurrentBatch, boxTemplates } = useShipmentContext();

  const [mode, setMode] = useState<BulkAssignMode>(BulkAssignMode.assignManually);
  const [selectedBoxes, setSelectedBoxes] = useState<boolean[]>([]);
  const [desiredSplitQuantities, setDesiredSplitQuantities] = useState<number[]>([]);
  const [totalDesiredSplitQuantity, setTotalDesiredSplitQuantity] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [allBoxesChecked, setAllBoxesChecked] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [quantityInOtherBoxes, setQuantityInOtherBoxes] = useState<number>(0);

  const quantityAssigned = getItemAssignedQuantity(currentBatch, packingGroupId, shipmentId, item?.batchItem.sku);

  useMemo(() => {
    const visibleQuantityAssigned = boxes.reduce((acc, box) => acc + (box.packingItems.find(i => i.batchItemId === item?.batchItem.id)?.quantity ?? 0), 0)
    setQuantityInOtherBoxes(quantityAssigned - visibleQuantityAssigned);
    setSelectedBoxes(boxes.map(() => true));
    setDesiredSplitQuantities(boxes.map(box => box.packingItems.find(i => i.batchItemId === item?.batchItem.id)?.quantity ?? 0));
    setTotalDesiredSplitQuantity(item?.quantity ?? 0);
  }, [mode, boxes, item]);

  let totalQuantityReserved = 0; // this is the quantity assigned, only in boxes that are not selected.
  let totalQuantityLeftToAssign = item?.quantity ?? 0;
  useMemo(() => {
    boxes.forEach((box, i) => {
      const itemWithinBox = box.packingItems.find(i => i.batchItemId === item?.batchItem.id);
      const quantity = itemWithinBox?.quantity ?? 0;
      if (!selectedBoxes[i]) totalQuantityReserved += quantity;
    });
    totalQuantityLeftToAssign = item?.quantity - totalQuantityReserved;
  }, [desiredSplitQuantities]);

  useMemo(() => {
    setTotalDesiredSplitQuantity(clamp(totalDesiredSplitQuantity, 0, totalQuantityLeftToAssign - totalQuantityReserved));
  }, [selectedBoxes]);

  const buildAssignmentRequest = async () => {
    setLoading(true);
    setErrorText(null);
  
    const updateBoxes: { box: FbaBox, dto: Partial<ShipmentUpdateBoxBody>, newItem: boolean, quantityDiff: number }[] = [];
    let totalQuantity = quantityInOtherBoxes;
    const requests: (() => Promise<FbaBox[]>)[] = [];

    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      const quantity = desiredSplitQuantities[i];

      const dto: ShipmentUpdateBoxBody = {
        items: [...box.packingItems],
        packingGroupId: packingGroupId,
        shipmentId: shipmentId,
      };
      let newItem: boolean = false;
      let quantityDiff: number = 0;

      const itemWithinBoxIndex = dto.items.findIndex(k => k.batchItemId === item.batchItem.id);
      if (itemWithinBoxIndex !== -1) {
        if (!quantity || quantity === 0) {
          // we have to remove the item from the box.
          dto.items.splice(itemWithinBoxIndex, 1);
        } else {
          if (quantity > dto.items[itemWithinBoxIndex].quantity) quantityDiff = quantity - dto.items[itemWithinBoxIndex].quantity;
          // we're updating the quantity of an item that is in the box.
          dto.items[itemWithinBoxIndex].quantity = quantity;
        }
      } else {
        if (!quantity || quantity === 0) {
          // the focused item isn't in this box, and the quantity we're trying to 'add' is 0. 
          // we don't want to do anything in this case.
          continue;
        }

        newItem = true;
        quantityDiff = quantity;

        // the item isn't in the box yet. add it.
        dto.items.push({
          batchItemId: item.batchItem.id,
          sku: item.batchItem.sku,
          quantity: quantity,
        });
      }

      totalQuantity += quantity;
    
      if (totalQuantity > item.quantity) { 
        setErrorText(`Total quantity distributed (${totalQuantity}) is greater than the amount in the shipment (${item.quantity}). Reduce the total quantity you're distributing to the boxes.`);
        setLoading(false);
        return;
      }

      updateBoxes.push({ box, dto, newItem, quantityDiff });

      requests.push(() => updateBoxV2(box.id, dto));
    }

    Promise.all(requests.map(f => f()))
      .then(_ => {
        let totalQuantityFNSKULabelsToPrint = 0;
        updateBoxes.forEach(ub => {
          Object.assign(ub.box.packingItems, ub.dto.items);
          const boxIndex = currentBatch.boxes.findIndex(b => b.id === ub.box.id);
          currentBatch.boxes[boxIndex] = {...currentBatch.boxes[boxIndex], packingGroupId: packingGroupId };

          if (currentBatch.printFNSKUAfterIncreaseQuantityInBox && (ub.quantityDiff > 0 || ub.newItem)) {
            totalQuantityFNSKULabelsToPrint += ub.quantityDiff;

          } else if (currentBatch.printFNSKUAfterAssigningToBox && ub.newItem) {
            totalQuantityFNSKULabelsToPrint += ub.quantityDiff;
          }
        });
        if (totalQuantityFNSKULabelsToPrint > 0) {
          const packingGroup = currentBatch.chosenPackingOption.packingGroups.find(pg => pg.inboundPackingGroupId === packingGroupId);
          const packingItem = packingGroup.items.find(k => k.msku === item.batchItem.sku); 
          printFNSKULabel(
            packingItem.fnsku,
            item.batchItem.name,
            item.batchItem.condition,
            totalQuantityFNSKULabelsToPrint,
            item.batchItem.expirationDate
          );
        }
        setCurrentBatch({...currentBatch});
        onClose();
        setLoading(false);
      })
      .catch(errors => {
        handleErrorResponseGenerically(errors);
        setLoading(false);
      });
  }

  return (
    <Modal 
      className={styles.modal}
      open={!!item}
      onOpenChange={open => {
        setErrorText(null);
        if (!loading && !open) onClose();
        if (!loading && open) buildAssignmentRequest();
      }}
    >
      <ModalHeader>
        <Flex direction="column" className={styles.modalHeader}>
          <Flex className={styles.productHeader}>
            <ProductImage imageUrl={item?.batchItem.image} alt={item?.batchItem.name} className={styles.productImage} />
            <Flex direction="column">
              <Tooltip
                title={item?.batchItem.name}
              >
                <h3 className={styles.truncateText}>{item?.batchItem.name}</h3>
              </Tooltip>
              <CopyToClipBoard value={item?.batchItem.asin} tooltipText="Copy ASIN">
                <Flex>
                  <span className="weak">ASIN:</span>
                  <span className="strong">&nbsp;{item?.batchItem.asin}</span>
                </Flex>
              </CopyToClipBoard>
            </Flex>
          </Flex>
          <Flex className={styles.headerToggleContainer}>
            <button
              className={styles.headerToggleButton + ` ${mode === BulkAssignMode.splitEvenly ? styles.headerToggleButtonSelected : ""}`}
              onClick={() => setMode(BulkAssignMode.splitEvenly)}
            >
              <span className="strong">Split evenly</span>
            </button>
            <button
              className={styles.headerToggleButton + ` ${mode === BulkAssignMode.assignManually ? styles.headerToggleButtonSelected : ""}`}
              onClick={() => setMode(BulkAssignMode.assignManually)}
            >
              <span className="strong">Assign manually</span>
            </button>
          </Flex>
        </Flex>
      </ModalHeader>

      <ModalBody>
        <Flex direction="column" className={styles.bulkAssignModal}>
          {!!errorText && (
            <Alert type="error" message={errorText} className={styles.alert} />
          )}
          <Flex className={styles.quantityAssignedRow} gap={2}>
            {quantityAssigned < item?.quantity && (<IconCaution />)}
            {quantityAssigned === item?.quantity && (<IconCheckCircle style={{color:"var(--sentiment-text-positive)"}} />)}
            <p>{quantityAssigned} of {item?.quantity} units have been assigned to boxes.</p>
          </Flex>

          {mode === BulkAssignMode.splitEvenly && (
            <>
              <Flex gap={2} align="center" className={styles.splitEvenlyContainer}>
                <Flex gap={1} align="center">
                  <span
                    className={`strong ${styles.splitEvenlyInput}`}
                  >
                    Split Quantity:
                  </span>
                  <InputNumber
                    className={styles.splitEvenlyInput}
                    min={0}
                    max={item?.quantity}
                    value={totalDesiredSplitQuantity}
                    wrapNumber={true}
                    onChange={(e) => {
                      const newValue = e;
                      setTotalDesiredSplitQuantity(newValue)
                    }}
                  />
                </Flex>
                <Button
                  type="secondary"
                  onClick={() => {
                    if (totalDesiredSplitQuantity < 0 || totalDesiredSplitQuantity > item.quantity) {
                      return; // @TODO error message? prevent bad user input better?
                    }

                    const boxCount = selectedBoxes.filter(b => !!b).length;
                    const split = Math.floor(totalDesiredSplitQuantity / boxCount);
                    let remainder = totalDesiredSplitQuantity % boxCount;

                    desiredSplitQuantities.forEach((_, i) => {
                      if (selectedBoxes[i]) {
                        desiredSplitQuantities[i] = split + (remainder > 0 ? 1 : 0);
                        remainder -= 1;
                      }
                    });
                    setDesiredSplitQuantities([...desiredSplitQuantities]);
                  }}
                >
                  Split across {selectedBoxes.filter(b => b).length} boxes
                </Button>
              </Flex>
              {boxes?.length > 0 && (
                <Flex>
                  <label className={styles.allBoxesCheckedLabel}>
                    <Checkbox
                      value={allBoxesChecked}
                      onChange={(e) => {
                        if (allBoxesChecked) {
                          setSelectedBoxes([...selectedBoxes.map(b => false)]);

                        } else {
                          setSelectedBoxes([...selectedBoxes.map(b => true)]);
                        }
                        
                        setAllBoxesChecked(!allBoxesChecked);
                      }}
                    />
                    {allBoxesChecked ? "De-select all boxes" : "Select all boxes"}
                  </label>
                </Flex>
              )}
            </>
          )}

          <Flex direction="column">
            {boxes.map((box, index) => {
              return (
                <Flex key={index} align="center" justify="space-between" className={styles.boxRow}>
                  <Flex align="center" gap={2}>
                    {mode === BulkAssignMode.splitEvenly && (
                      <Checkbox
                        value={selectedBoxes[index]}
                        onChange={(e) => {
                          selectedBoxes[index] = !selectedBoxes[index];
                          if (!selectedBoxes[index]) {
                            desiredSplitQuantities[index] = 0;
                            setDesiredSplitQuantities([...desiredSplitQuantities]);
                          }
                          setSelectedBoxes([...selectedBoxes]);
                        }}
                      />
                    )}
                    
                    <IconBox />

                    <span>Box {box.displayIndex.toString()}</span>
                  </Flex>

                  <InputNumber
                    value={desiredSplitQuantities[index]}
                    min={0}
                    max={totalQuantityLeftToAssign}
                    wrapNumber={true}
                    onChange={(e) => {
                      desiredSplitQuantities[index] = e;
                      setDesiredSplitQuantities([...desiredSplitQuantities]);
                    }}
                  />
                </Flex>
              )
            })}
          </Flex>
        </Flex>
      </ModalBody>

      <ModalFooter>
        <Flex gap={2} justify="space-between" className={styles.modalFooter}>
          {
            loading ? <LoadingSpinner /> 
            : <>
              <CreateNewBoxButton createBoxes={createBoxes} />
              <Flex gap={2}>
                <Button type="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={buildAssignmentRequest}>Save</Button>
              </Flex>
            </>
          }
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
